import type { Chart, ChartData, ChartEvent, ChartOptions, LegendItem, Plugin, TooltipItem } from 'chart.js';
import { generateColors } from '@shared/modules/chartjs-defaults';
import { QuestionType, QuestionReportType, type Nps } from '../../SurveysTypes.d';

export interface ChartOptionEntry {
    Value: string;
    Ordinal: number;
    ResponseCount: number;
    NumericValue?: number | null;
    UseMaxAnswers?: boolean;
    MaxAnswers?: number;
    QuestionOptionId?: string;
    CultureCode?: string;
}

export type NpsGroupName = 'Detractors' | 'Passives' | 'Promoters';

export type QuestionChartKind = 'horizontalBar' | 'pie' | 'nps' | 'csat';

function setPointerCursor(evt: ChartEvent, isPointer: boolean): void {
    const target = evt.native?.target as HTMLElement | undefined;
    if (target) target.style.cursor = isPointer ? 'pointer' : 'default';
}

export function resolveChartKind(type: number, reportType: number | undefined, hasNumericValue: boolean): QuestionChartKind {
    const isNps = reportType === QuestionReportType.Nps;
    const isCSAT = reportType === QuestionReportType.Csat;

    if (type !== QuestionType.Single || (hasNumericValue && !isNps && !isCSAT)) return 'horizontalBar';
    if (isNps) return 'nps';
    if (isCSAT) return 'csat';
    return 'pie';
}

export function resolveSortDefaults(hasNumericValue: boolean, kind: QuestionChartKind) {
    return {
        property: 'Ordinal' as 'Ordinal' | 'Value' | 'NumericValue' | 'ResponseCount',
        direction: (hasNumericValue && kind !== 'nps' && kind !== 'csat' ? 'desc' : 'asc') as 'asc' | 'desc',
    };
}

export function sortOptions(
    options: ChartOptionEntry[],
    property: 'Ordinal' | 'Value' | 'NumericValue' | 'ResponseCount',
    direction: 'asc' | 'desc'
): ChartOptionEntry[] {
    const sorted = [...options].sort((l, r) => {
        const lv = property === 'Value' ? l.Value : (l[property] ?? 0);
        const rv = property === 'Value' ? r.Value : (r[property] ?? 0);
        if (lv === rv) return 0;
        return lv > rv ? 1 : -1;
    });
    return direction === 'desc' ? sorted.reverse() : sorted;
}

function truncate(text: string, max: number): string {
    return text.length > max ? `${text.slice(0, max)}...` : text;
}

function wrapLabel(text: string, rowLength: number, maxRows: number): string[] {
    const words = (text ?? '').split(' ');
    const rows: string[] = [];
    let row = '';
    let wordIndex = 0;

    for (; wordIndex < words.length; wordIndex++) {
        if (rows.length === maxRows) break;
        row += `${words[wordIndex]} `;
        if (row.length > rowLength || wordIndex === words.length - 1) {
            rows.push(row.trim());
            row = '';
        }
    }

    if (wordIndex < words.length && rows.length) {
        rows[rows.length - 1] += '...';
    }

    return rows;
}

function wrapFullText(text: string, rowLength: number): string[] {
    const words = (text ?? '').split(' ');
    const rows: string[] = [];
    let row = '';

    for (const word of words) {
        row += `${word} `;
        if (row.length > rowLength) {
            rows.push(row.trim());
            row = '';
        }
    }
    if (row) rows.push(row.trim());

    return rows;
}

export function horizontalBarRowHeight(options: ChartOptionEntry[]): number {
    const maxLines = Math.max(1, ...options.map((o) => wrapLabel(o.Value ?? '', 20, 3).length + 1));
    return 32 + maxLines * 16;
}

export function horizontalBarData(options: ChartOptionEntry[], responseCount: number): ChartData<'bar'> {
    return {
        labels: options.map((o) => {
            const pct = responseCount > 0 ? Math.round((o.ResponseCount / responseCount) * 100) : 0;
            const maxAnswers = o.UseMaxAnswers && o.MaxAnswers != null ? `/${o.MaxAnswers}` : '';
            const countLine = `${$Resources.ResponseCountLabel}: ${o.ResponseCount}${maxAnswers} (${pct}%)`;
            return [...wrapLabel(o.Value ?? '', 20, 3), countLine];
        }) as unknown as string[],
        datasets: [
            {
                data: options.map((o) => o.ResponseCount),
                backgroundColor: 'rgba(31,169,176,1)',
                hoverBackgroundColor: 'rgba(31,169,176,.9)',
                barPercentage: 0.9,
                categoryPercentage: 0.95,
            },
        ],
    };
}

export function horizontalBarOptions(options: ChartOptionEntry[], responseCount: number, onSelect?: (option: ChartOptionEntry) => void): ChartOptions<'bar'> {
    return {
        indexAxis: 'y',
        layout: { padding: { left: 0, right: 16, top: 0, bottom: 16 } },
        onClick: onSelect
            ? (_evt, elements) => {
                  const option = elements[0] && options[elements[0].index];
                  if (option) onSelect(option);
              }
            : undefined,
        onHover: onSelect ? (evt, elements) => setPointerCursor(evt, elements.length > 0) : undefined,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    title: (items: TooltipItem<'bar'>[]) => {
                        const option = options[items[0].dataIndex];
                        return wrapFullText(option.Value ?? '', 40);
                    },
                    label: (item: TooltipItem<'bar'>) => {
                        const option = options[item.dataIndex];
                        const pct = responseCount > 0 ? Math.round((option.ResponseCount / responseCount) * 100) : 0;
                        const maxAnswers = option.UseMaxAnswers && option.MaxAnswers != null ? `/${option.MaxAnswers}` : '';
                        return `${$Resources.ResponseCountLabel}: ${option.ResponseCount}${maxAnswers} (${pct}%)`;
                    },
                },
            },
        },
        scales: {
            x: {
                beginAtZero: true,
                grid: { color: '#e9e9e9' },
                ticks: { display: false },
            },
            y: {
                grid: { display: false },
                ticks: { font: { weight: 'bold' as const } },
            },
        },
    };
}

function legendLabelsWithCount(options: ChartOptionEntry[], responseCount: number) {
    return (chart: Chart): LegendItem[] => {
        const dataset = chart.data.datasets[0];
        const backgroundColors = dataset.backgroundColor as string[];

        return (chart.data.labels as string[]).map((label, i) => {
            const option = options[i];
            const pct = responseCount > 0 ? Math.round((option.ResponseCount / responseCount) * 100) : 0;
            const maxAnswers = option.UseMaxAnswers && option.MaxAnswers != null ? `/${option.MaxAnswers}` : '';

            return {
                text: `${label}, ${option.ResponseCount}${maxAnswers} (${pct}%)`,
                fillStyle: backgroundColors[i],
                strokeStyle: backgroundColors[i],
                index: i,
            };
        });
    };
}

export function pieData(options: ChartOptionEntry[]): ChartData<'pie'> {
    return {
        labels: options.map((o) => truncate(o.Value ?? '', 80)),
        datasets: [
            {
                data: options.map((o) => o.ResponseCount),
                backgroundColor: generateColors(options.length || 1),
            },
        ],
    };
}

export function pieOptions(options: ChartOptionEntry[], responseCount: number, onSelect?: (option: ChartOptionEntry) => void): ChartOptions<'pie'> {
    return {
        layout: { padding: { left: 10, right: 10, top: 0, bottom: 20 } },
        onClick: onSelect
            ? (_evt, elements) => {
                  const option = elements[0] && options[elements[0].index];
                  if (option) onSelect(option);
              }
            : undefined,
        onHover: onSelect ? (evt, elements) => setPointerCursor(evt, elements.length > 0) : undefined,
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                labels: { boxWidth: 14, font: { weight: 'bold' as const }, padding: 15, generateLabels: legendLabelsWithCount(options, responseCount) },
            },
            tooltip: {
                callbacks: {
                    label: (item: TooltipItem<'pie'>) => {
                        const option = options[item.dataIndex];
                        const pct = responseCount > 0 ? Math.round((option.ResponseCount / responseCount) * 100) : 0;
                        const maxAnswers = option.UseMaxAnswers && option.MaxAnswers != null ? `/${option.MaxAnswers} ` : '';
                        return `${$Resources.ResponseCountLabel}: ${option.ResponseCount}${maxAnswers} (${pct}%)`;
                    },
                },
            },
        },
    };
}

export function npsDoughnutData(nps: Nps): ChartData<'doughnut'> {
    return {
        labels: [$Resources.NpsDetractorsLabel, $Resources.NpsPassivesLabel, $Resources.NpsPromotersLabel],
        datasets: [
            {
                data: [nps.DetractorPercentage, nps.PassivePercentage, nps.PromoterPercentage],
                backgroundColor: ['rgba(238,91,86,1)', 'rgba(221,221,221,1)', 'rgba(31,169,176,1)'],
                hoverBackgroundColor: ['rgba(238,91,86,.9)', 'rgba(221,221,221,.9)', 'rgba(31,169,176,.9)'],
                borderWidth: 5,
                hoverBorderColor: '#fff',
            },
        ],
    };
}

const npsGroupOrder: NpsGroupName[] = ['Detractors', 'Passives', 'Promoters'];

export function npsDoughnutOptions(onSelectGroup?: (group: NpsGroupName) => void): ChartOptions<'doughnut'> {
    return {
        layout: { padding: { left: 10, right: 10, top: 0, bottom: 20 } },
        rotation: -90,
        circumference: 180,
        onClick: onSelectGroup
            ? (_evt, elements) => {
                  const group = elements[0] && npsGroupOrder[elements[0].index];
                  if (group) onSelectGroup(group);
              }
            : undefined,
        onHover: onSelectGroup ? (evt, elements) => setPointerCursor(evt, elements.length > 0) : undefined,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (item: TooltipItem<'doughnut'>) => `${item.formattedValue}%`,
                },
            },
        },
    };
}

const npsScoreColors = [
    'rgba(238,91,86,1)',
    'rgba(238,91,86,1)',
    'rgba(238,91,86,1)',
    'rgba(238,91,86,1)',
    'rgba(238,91,86,1)',
    'rgba(238,91,86,1)',
    'rgba(238,91,86,1)',
    'rgba(221,221,221,1)',
    'rgba(221,221,221,1)',
    'rgba(87,199,185,1)',
    'rgba(87,199,185,1)',
];

export function npsScoreBarData(options: ChartOptionEntry[]): ChartData<'bar'> {
    return {
        labels: options.map((o) => o.Value),
        datasets: [
            {
                data: options.map((o) => o.ResponseCount),
                backgroundColor: options.map((_o, i) => npsScoreColors[i % npsScoreColors.length]),
            },
        ],
    };
}

export function npsScoreBarOptions(options: ChartOptionEntry[], responseCount: number, onSelect?: (option: ChartOptionEntry) => void): ChartOptions<'bar'> {
    return {
        layout: { padding: { left: 10, right: 10, top: 0, bottom: 20 } },
        onClick: onSelect
            ? (_evt, elements) => {
                  const option = elements[0] && options[elements[0].index];
                  if (option) onSelect(option);
              }
            : undefined,
        onHover: onSelect ? (evt, elements) => setPointerCursor(evt, elements.length > 0) : undefined,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (item: TooltipItem<'bar'>) => {
                        const option = options[item.dataIndex];
                        const pct = responseCount > 0 ? Math.round((option.ResponseCount / responseCount) * 100) : 0;
                        return `${$Resources.ResponseCountLabel}: ${option.ResponseCount} (${pct}%)`;
                    },
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { font: { weight: 'bold' as const }, autoSkip: false },
            },
            y: {
                beginAtZero: true,
                grid: { color: '#e9e9e9' },
                ticks: { precision: 0 },
            },
        },
    };
}

const csatColors = ['#e44c51', '#d77d34', '#dfb63c', '#85b563', '#2ca05e'];

export function csatDoughnutData(options: ChartOptionEntry[]): ChartData<'doughnut'> {
    return {
        labels: options.map((o) => truncate(o.Value ?? '', 80)),
        datasets: [
            {
                data: options.map((o) => o.ResponseCount),
                backgroundColor: options.map((_o, i) => csatColors[i % csatColors.length]),
            },
        ],
    };
}

export function csatDoughnutOptions(
    options: ChartOptionEntry[],
    responseCount: number,
    onSelect?: (option: ChartOptionEntry) => void
): ChartOptions<'doughnut'> {
    return {
        layout: { padding: { left: 10, right: 10, top: 0, bottom: 20 } },
        rotation: -90,
        circumference: 180,
        onClick: onSelect
            ? (_evt, elements) => {
                  const option = elements[0] && options[elements[0].index];
                  if (option) onSelect(option);
              }
            : undefined,
        onHover: onSelect ? (evt, elements) => setPointerCursor(evt, elements.length > 0) : undefined,
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                labels: { boxWidth: 14, font: { weight: 'bold' as const }, padding: 15, generateLabels: legendLabelsWithCount(options, responseCount) },
            },
            tooltip: {
                callbacks: {
                    label: (item: TooltipItem<'doughnut'>) => {
                        const option = options[item.dataIndex];
                        const pct = responseCount > 0 ? Math.round((option.ResponseCount / responseCount) * 100) : 0;
                        return `${$Resources.ResponseCountLabel}: ${option.ResponseCount} (${pct}%)`;
                    },
                },
            },
        },
    };
}

export function centerTextPlugin(getText: () => string): Plugin<'doughnut'> {
    return {
        id: 'centerText',
        afterDraw(chart) {
            const text = getText();
            if (!text) return;

            const { ctx, chartArea } = chart;
            ctx.save();
            ctx.font = 'bold 20px "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif';
            ctx.fillStyle = '#1f1c26';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const x = (chartArea.left + chartArea.right) / 2;
            const y = chartArea.bottom - (chartArea.bottom - chartArea.top) * 0.12;
            ctx.fillText(text, x, y);
            ctx.restore();
        },
    };
}
